import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import lineageApiService from '../../api/lineageApiService';
import { userAPI } from '../../api/enhancedApiService';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { 
  CheckCircleIcon, 
  ChartBarIcon,
  StarIcon,
  UserGroupIcon,
  CalendarIcon,
  BellIcon,
  ShareIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  TrophyIcon,
  RocketLaunchIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const OnboardingDashboardPage = () => {
  const navigate = useNavigate();
  // ConnectionContext caches its connection list on mount and only auto-
  // refreshes every 5 minutes. Connections created during onboarding go
  // through connectionsAPI.create directly (bypassing the context's
  // createConnection), so the dashboard renders with an empty list until
  // we manually refresh.
  //
  // Use refreshConnectionsNow (non-debounced) — refreshConnections is
  // debounced 500ms, so `await refreshConnections()` doesn't actually
  // wait for the inner work to complete. The navigate fires before the
  // fresh list arrives and the dashboard mounts with stale state.
  // 2026-05-11 testing surfaced this as "have to refresh again to get
  // connections to show up."
  const { refreshConnectionsNow } = useConnection();
  const [onboardingData, setOnboardingData] = useState(null);
  const [prioritizedIssues, setPrioritizedIssues] = useState(null);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [completionStats, setCompletionStats] = useState({});
  const [lineageEnabled, setLineageEnabled] = useState(false);
  const [realIssues, setRealIssues] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOnboardingSummary();
  }, []);

  const loadOnboardingSummary = async () => {
    setLoading(true);
    
    try {
      // Load all onboarding data from localStorage
      const discoveryResult = JSON.parse(localStorage.getItem('discoveryResult') || '{}');
      const validationResults = JSON.parse(localStorage.getItem('validationResults') || '[]');
      const issues = JSON.parse(localStorage.getItem('prioritizedIssues') || '{}');
      const priorities = JSON.parse(localStorage.getItem('selectedPriorities') || '["high"]');
      const selectedTables = JSON.parse(localStorage.getItem('selectedTables') || '[]');
      
      setOnboardingData(discoveryResult);
      setPrioritizedIssues(issues);
      setSelectedPriorities(priorities);
      
      // Try to load real lineage and issue data
      try {
        const dashboardSummary = await lineageApiService.getDashboardSummary();
        
        if (dashboardSummary.success) {
          setLineageEnabled(dashboardSummary.data.lineage.lineage_enabled);
          setRealIssues(dashboardSummary.data.issues);
        }
        
        // If we have a connection ID, try to discover lineage
        const connectionId = localStorage.getItem('onboardingConnectionId');
        if (connectionId && !dashboardSummary.data.lineage.lineage_enabled) {
          console.log('Triggering lineage discovery for connection:', connectionId);
          await lineageApiService.discoverLineage(connectionId);
          setLineageEnabled(true);
        }
      } catch (error) {
        console.warn('Could not load lineage/issue data:', error);
        // Continue with onboarding data only
      }
      
      // Calculate completion stats (combining onboarding and real data if available)
      const totalTables = discoveryResult.all_tables?.length || discoveryResult.tables?.length || 0;
      const selectedTableCount = selectedTables.length;
      const totalValidations = validationResults.length;
      const passedValidations = validationResults.filter(r => r.passed).length;
      const totalRows = (discoveryResult.all_tables || discoveryResult.tables || [])
        .reduce((sum, table) => sum + (table.row_count || 0), 0);
      
      // Only show real issue counts. The validation execution during
      // onboarding was simulated (Math.random) so the localStorage
      // `prioritizedIssues` blob would over-report. Issues are created
      // by the scheduled validation worker after the first scan.
      const hasRealIssueData = Boolean(realIssues && realIssues.issues);
      const highPriorityIssues = hasRealIssueData ? (realIssues.issues.high_priority?.length || 0) : 0;
      const mediumPriorityIssues = hasRealIssueData ? (realIssues.issues.medium_priority?.length || 0) : 0;
      const lowPriorityIssues = hasRealIssueData ? (realIssues.issues.low_priority?.length || 0) : 0;

      setCompletionStats({
        totalTables,
        selectedTableCount,
        totalValidations,
        passedValidations,
        totalRows,
        totalIssues: highPriorityIssues + mediumPriorityIssues + lowPriorityIssues,
        highPriorityIssues,
        mediumPriorityIssues,
        lowPriorityIssues,
        hasRealIssueData,
        dataHealthScore: totalValidations > 0 ? Math.round((passedValidations / totalValidations) * 100) : 100
      });
      
    } catch (error) {
      console.error('Error loading onboarding summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToDashboard = async () => {
    // Clear onboarding data
    localStorage.removeItem('onboardingData');
    localStorage.removeItem('onboardingConnectionId');
    localStorage.removeItem('discoveryResult');
    localStorage.removeItem('selectedTables');
    localStorage.removeItem('validationResults');
    localStorage.removeItem('prioritizedIssues');
    localStorage.removeItem('selectedPriorities');

    // Mark first_login_completed so OnboardingRouter routes future visits
    // directly to /dashboard instead of bouncing through /welcome. Without
    // this the user gets trapped in the welcome flow after onboarding even
    // though they've already done everything that flow was designed to do.
    try {
      await userAPI.markFirstLoginComplete();
    } catch (error) {
      console.warn('[OnboardingDashboard] markFirstLoginComplete failed; falling back to localStorage', error);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          // OnboardingRouter falls back to this localStorage key if the
          // API is unreachable.
          const userId = JSON.parse(localStorage.getItem('sparvi-supabase-auth') || '{}')?.user?.id;
          if (userId) {
            localStorage.setItem(`onboarding_completed_${userId}`, 'true');
          }
        }
      } catch {
        // Best-effort — nothing actionable if both paths fail.
      }
    }

    // Force the connection list to refresh so the dashboard sees the
    // connection the user just created in onboarding. Without this, the
    // user lands on /dashboard with an empty connection picker and has to
    // manually refresh the page. Awaited so the dashboard mounts after
    // the new list is in state.
    try {
      await refreshConnectionsNow();
    } catch (error) {
      console.warn('[OnboardingDashboard] refreshConnectionsNow failed; dashboard may need manual refresh', error);
    }

    navigate('/dashboard');
  };

  const handleStartHighPriorityReview = async () => {
    // Navigate to issues page with high priority filter
    navigate('/issues?priority=high');
  };

  const handleConfigureNotifications = async () => {
    // Navigate to automation settings page
    navigate('/settings/automation');
  };

  const handleSetupQuickWin = (winTitle) => {
    const quickWinActions = {
      'Real-time alerts configured': handleConfigureNotifications,
      'Stakeholder dashboard sharing': handleShareDashboard,
      'Historical trend tracking': () => navigate('/analytics'),
      'Automated monitoring is active': () => navigate('/validations')
    };

    const action = quickWinActions[winTitle];
    if (action) {
      action();
    } else {
      // Default fallback to settings if no specific action
      navigate('/settings');
    }
  };

  const handleInviteTeam = () => {
    // Navigate to admin settings for team management
    navigate('/admin');
  };

  const handleConfigureAlerts = () => {
    handleConfigureNotifications();
  };

  const handleShareDashboard = () => {
    // Copy dashboard URL to clipboard and show notification
    const dashboardUrl = `${window.location.origin}/dashboard`;
    navigator.clipboard.writeText(dashboardUrl).then(() => {
      // Show success message
      const notification = document.createElement('div');
      notification.innerHTML = `
        <div class="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            Dashboard URL copied to clipboard!
          </div>
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 3000);
    }).catch(() => {
      // Fallback - navigate to settings where sharing options might be
      navigate('/settings');
    });
  };

  const getNextSteps = () => {
    const steps = [];

    if (completionStats.hasRealIssueData && completionStats.highPriorityIssues > 0) {
      steps.push({
        title: 'Review high-priority data quality issues',
        description: `Address ${completionStats.highPriorityIssues} critical issues affecting business operations`,
        priority: 'high',
        timeframe: 'Today',
        action: 'Start Review'
      });
    } else {
      steps.push({
        title: 'Review your active validation rules',
        description: `${completionStats.totalValidations} rules are configured. Issues will appear here after the first scan runs.`,
        priority: 'medium',
        timeframe: 'Today',
        action: 'View Validations'
      });
    }

    if (completionStats.hasRealIssueData && completionStats.mediumPriorityIssues > 0) {
      steps.push({
        title: 'Investigate medium-priority issues',
        description: `Review ${completionStats.mediumPriorityIssues} issues for potential improvements`,
        priority: 'medium',
        timeframe: 'This Week',
        action: 'Schedule Review'
      });
    }

    steps.push({
      title: 'Set up team notifications',
      description: 'Configure Slack or email alerts to keep your team informed',
      priority: 'medium',
      timeframe: 'Today',
      action: 'Configure'
    });

    steps.push({
      title: 'Schedule weekly data quality review',
      description: 'Regular check-ins to maintain data quality standards',
      priority: 'low',
      timeframe: 'This Week',
      action: 'Schedule'
    });

    return steps;
  };

  const getQuickWins = () => [
    {
      title: 'Automated monitoring is active',
      description: `${completionStats.totalValidations} rules monitoring your data 24/7`,
      completed: true
    },
    {
      title: 'Real-time alerts configured',
      description: 'Get notified immediately when new issues appear',
      completed: false
    },
    {
      title: 'Historical trend tracking',
      description: 'Monitor data quality improvements over time',
      completed: true
    },
    {
      title: 'Stakeholder dashboard sharing',
      description: 'Share results with business stakeholders',
      completed: false
    }
  ];

  const getAccomplishments = () => {
    const items = [
      'Connected to your database',
      `Analyzed ${completionStats.totalTables} tables across your data warehouse`,
      `Generated ${completionStats.totalValidations} intelligent validation rules`,
    ];
    if (completionStats.hasRealIssueData && completionStats.totalIssues > 0) {
      items.push(`Discovered ${completionStats.totalIssues} data quality issues`);
      items.push('Prioritized fixes by business impact');
    } else {
      items.push('Queued first validation scan');
    }
    items.push('Created your personalized data quality dashboard');
    return items;
  };

  if (loading || !onboardingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {loading ? 'Loading your dashboard...' : 'Setting up lineage discovery...'}
          </h2>
          {loading && (
            <div className="mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">
                Connecting to lineage and issue tracking systems...
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <TrophyIcon className="h-16 w-16 text-yellow-500" />
              <SparklesIcon className="h-6 w-6 text-yellow-400 absolute -top-1 -right-1" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Welcome to Your Data Quality Command Center
          </h1>
          <p className="text-xl text-gray-600">
            You&apos;ve successfully set up data quality monitoring.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {lineageEnabled && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                Lineage Discovery Active
              </div>
            )}
          </div>
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Overview */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Data Health Overview */}
            <div className="bg-white rounded-lg shadow-xl p-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Data Health Overview</h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600">{completionStats.dataHealthScore}%</div>
                  <div className="text-sm text-blue-800">Overall Health</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-3xl font-bold text-green-600">{completionStats.selectedTableCount}</div>
                  <div className="text-sm text-green-800">Tables Monitored</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-3xl font-bold text-purple-600">{completionStats.totalValidations}</div>
                  <div className="text-sm text-purple-800">Active Rules</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-3xl font-bold text-yellow-600">{completionStats.totalIssues}</div>
                  <div className="text-sm text-yellow-800">Issues Found</div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                {completionStats.hasRealIssueData ? (
                  <>
                    <h3 className="font-medium text-gray-900 mb-2">Issues Requiring Attention:</h3>
                    <div className="space-y-2">
                      {completionStats.highPriorityIssues > 0 && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                            <span className="text-sm text-gray-700">High Priority ({completionStats.highPriorityIssues})</span>
                          </div>
                          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Fix these first</span>
                        </div>
                      )}
                      {completionStats.mediumPriorityIssues > 0 && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <InformationCircleIcon className="h-5 w-5 text-yellow-500 mr-2" />
                            <span className="text-sm text-gray-700">Medium Priority ({completionStats.mediumPriorityIssues})</span>
                          </div>
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Review this week</span>
                        </div>
                      )}
                      {completionStats.lowPriorityIssues > 0 && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-2" />
                            <span className="text-sm text-gray-700">Low Priority ({completionStats.lowPriorityIssues})</span>
                          </div>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Monitor monthly</span>
                        </div>
                      )}
                      {completionStats.totalIssues === 0 && (
                        <div className="flex items-center text-sm text-green-700">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                          No issues detected on the most recent scan.
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="font-medium text-gray-900 mb-2">First scan pending</h3>
                    <p className="text-sm text-gray-600">
                      Validations are configured and queued. The first scan will run on the schedule
                      configured for your plan. Issue counts and a real health score will appear here
                      once the worker completes the first run.
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Your Next Steps */}
            <div className="bg-white rounded-lg shadow-xl p-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Your Next Steps</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CalendarIcon className="h-5 w-5 text-blue-600 mr-2" />
                    Today:
                  </h3>
                  <div className="space-y-3">
                    {getNextSteps().filter(step => step.timeframe === 'Today').map((step, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{step.title}</h4>
                          <p className="text-sm text-gray-600">{step.description}</p>
                        </div>
                        <button
                          onClick={() => {
                            if (step.action === 'Start Review') return handleStartHighPriorityReview();
                            if (step.action === 'Configure') return handleConfigureNotifications();
                            if (step.action === 'View Validations') return navigate('/validations');
                            return null;
                          }}
                          className={`px-4 py-2 text-sm font-medium rounded-md ${
                            step.priority === 'high'
                              ? 'bg-red-600 text-white hover:bg-red-700'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}>
                          {step.action}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CalendarIcon className="h-5 w-5 text-green-600 mr-2" />
                    This Week:
                  </h3>
                  <div className="space-y-2">
                    {getNextSteps().filter(step => step.timeframe === 'This Week').map((step, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 text-sm">{step.title}</h4>
                          <p className="text-xs text-gray-600">{step.description}</p>
                        </div>
                        <button 
                          onClick={() => step.action === 'Schedule Review' ? handleStartHighPriorityReview() : step.action === 'Schedule' ? handleConfigureNotifications() : null}
                          className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700">
                          {step.action}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Success Metrics */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <TrophyIcon className="h-6 w-6 text-yellow-500 mr-2" />
                What We Accomplished in 15 Minutes
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getAccomplishments().map((accomplishment, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{accomplishment}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-white border border-green-200 rounded-lg">
                <p className="text-lg font-medium text-green-900 text-center">
                  Monitoring {completionStats.selectedTableCount} {completionStats.selectedTableCount === 1 ? 'table' : 'tables'} with {completionStats.totalValidations} {completionStats.totalValidations === 1 ? 'validation rule' : 'validation rules'}.
                </p>
                <p className="text-sm text-green-700 text-center mt-1">
                  Validations run on the schedule configured for your plan. Issues will appear after the first scan completes.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Quick Actions */}
          <div className="space-y-6">
            
            {/* Quick Wins */}
            <div className="bg-white rounded-lg shadow-xl p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Wins Available</h2>
              
              <div className="space-y-4">
                {getQuickWins().map((win, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 mr-3">
                      <CheckCircleIcon className={`h-5 w-5 ${
                        win.completed ? 'text-green-500' : 'text-gray-300'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-medium ${
                        win.completed ? 'text-gray-900' : 'text-gray-600'
                      }`}>
                        {win.title}
                      </h3>
                      <p className="text-sm text-gray-600">{win.description}</p>
                      {!win.completed && (
                        <button 
                          onClick={() => handleSetupQuickWin(win.title)}
                          className="mt-2 text-sm text-blue-600 hover:text-blue-800">
                          Set up now →
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-lg shadow-xl p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Get Started</h2>
              
              <div className="space-y-3">
                {completionStats.hasRealIssueData && completionStats.highPriorityIssues > 0 ? (
                  <button
                    onClick={handleStartHighPriorityReview}
                    className="w-full flex items-center justify-center px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700">
                    <PlayIcon className="h-5 w-5 mr-2" />
                    Start with High Priority Issues
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/validations')}
                    className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700">
                    <PlayIcon className="h-5 w-5 mr-2" />
                    Review Active Validations
                  </button>
                )}

                <button
                  onClick={handleInviteTeam}
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700">
                  <UserGroupIcon className="h-5 w-5 mr-2" />
                  Invite Team Members
                </button>

                <button
                  onClick={handleConfigureAlerts}
                  className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700">
                  <BellIcon className="h-5 w-5 mr-2" />
                  Configure Alerts
                </button>

                <button
                  onClick={handleShareDashboard}
                  className="w-full flex items-center justify-center px-4 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700">
                  <ShareIcon className="h-5 w-5 mr-2" />
                  Share Dashboard
                </button>
              </div>
            </div>

            {/* Continue to Main App */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6 text-center">
              <RocketLaunchIcon className="h-12 w-12 mx-auto mb-4 text-blue-100" />
              <h3 className="text-lg font-semibold mb-2">Ready to dive deeper?</h3>
              <p className="text-blue-100 mb-4 text-sm">
                Explore your full data quality dashboard with advanced analytics, custom rules, and team collaboration features.
              </p>
              <button
                onClick={handleGoToDashboard}
                className="w-full bg-white text-blue-600 font-medium py-3 px-4 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Go to Main Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingDashboardPage;